<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import AdvupSelect from 'dashboard/components-next/AdvupSelect.vue';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import wootConstants from 'dashboard/constants/globals';

const emit = defineEmits(['change']);

const { t } = useI18n();
const { showAlert } = useAlert();
const store = useStore();
const { updateUISettings } = useUISettings();

const chatSortFilter = useMapGetter('getChatSortFilter');
const chatStatusFilter = useMapGetter('getChatStatusFilter');

const SORT = wootConstants.SORT_BY_TYPE;

// opções estilo Digisac + Prioridade (lead quente do Lumos primeiro)
const opcoesDaFileira = computed(() => [
  { label: t('CHAT_LIST.SORT_ROW.PRIORITY'), value: SORT.PRIORITY_DESC },
  { label: t('CHAT_LIST.SORT_ROW.NEWEST'), value: SORT.LAST_ACTIVITY_AT_DESC },
  { label: t('CHAT_LIST.SORT_ROW.OLDEST'), value: SORT.LAST_ACTIVITY_AT_ASC },
  {
    label: t('CHAT_LIST.SORT_ROW.LONGEST_WAIT'),
    value: SORT.WAITING_SINCE_ASC,
  },
  {
    label: t('CHAT_LIST.SORT_ROW.SHORTEST_WAIT'),
    value: SORT.WAITING_SINCE_DESC,
  },
]);

const currentValue = computed(
  () => chatSortFilter.value || SORT.LAST_ACTIVITY_AT_DESC
);

// A preferência gravada pode ser uma ordenação que a fileira não oferece
// ("criadas primeiro", de antes desta fileira existir). Sem ela na lista, o
// seletor mostraria "Mais recentes" enquanto a lista segue em outra ordem —
// então a escolha salva entra, com o nome dela, até a pessoa trocar.
const nomesForaDaFileira = computed(() => ({
  [SORT.CREATED_AT_DESC]: t('CHAT_LIST.SORT_ORDER_ITEMS.created_at_desc.TEXT'),
  [SORT.CREATED_AT_ASC]: t('CHAT_LIST.SORT_ORDER_ITEMS.created_at_asc.TEXT'),
  [SORT.PRIORITY_ASC]: t('CHAT_LIST.SORT_ORDER_ITEMS.priority_asc.TEXT'),
  [SORT.PRIORITY_DESC_CREATED_AT_ASC]: t(
    'CHAT_LIST.SORT_ORDER_ITEMS.priority_desc_created_at_asc.TEXT'
  ),
}));

const options = computed(() => {
  const lista = opcoesDaFileira.value;
  const nome = nomesForaDaFileira.value[currentValue.value];
  if (!nome || lista.some(o => o.value === currentValue.value)) return lista;
  return [...lista, { label: nome, value: currentValue.value }];
});
const select = async value => {
  if (value === currentValue.value) return;
  // O `dispatch` sem `catch` virava rejeição não tratada: trocar a ordenação
  // com a rede fora deixava a lista como estava, sem uma palavra.
  try {
    await store.dispatch('setChatSortFilter', value);
  } catch {
    showAlert(t('CHAT_LIST.SORT_ROW.FAILED'));
    return;
  }
  updateUISettings({
    conversations_filter_by: {
      status: chatStatusFilter.value || wootConstants.STATUS_TYPE.OPEN,
      order_by: value,
    },
  });
  emit('change', value, 'sort');
};
</script>

<template>
  <div class="csr-row">
    <span class="csr-rot" aria-hidden="true">
      {{ t('CHAT_LIST.SORT_ROW.LABEL') }}
    </span>
    <!-- A lista de opções é o AdvupSelect, o único seletor do produto: era um
         botão com menu escritos à mão aqui dentro, com raio, sombra e estado
         de escolha próprios — e sem teclado. -->
    <AdvupSelect
      :model-value="currentValue"
      :options="options"
      :rotulo="t('CHAT_LIST.SORT_ROW.BUTTON')"
      min-width="150px"
      class="csr-sel"
      @update:model-value="select"
    />
  </div>
</template>

<style scoped>
/* A fileira de ordenação da lista de conversas. */
.csr-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--esp-2);
  padding: var(--esp-2) var(--esp-3);
  border-bottom: 1px solid rgb(var(--slate-3));
  /* A mesma superfície da lista: era `--solid-1`, o cinza-chumbo que no tema
     escuro virava uma faixa de outro preto dentro da coluna navy. */
  background: rgb(var(--surface-2));
}

.csr-rot {
  flex-shrink: 0;
  font-size: var(--txt-nota);
  font-weight: var(--peso-rotulo);
  color: rgb(var(--slate-10));
}

/* O seletor aqui é fileira de lista, não campo de formulário: 30px de altura
   em vez dos 38 do modo formulário, e sem o rebaixo cinza — o rótulo à
   esquerda já diz o que ele é. */
.csr-sel :deep(.adv-sel-trigger) {
  min-height: 30px;
  padding-block: 0;
  border-color: transparent;
  background: none;
  color: rgb(var(--blue-11));
}

.csr-sel :deep(.adv-sel-trigger:hover) {
  border-color: rgb(var(--slate-6));
  background: rgb(var(--slate-2));
}
</style>
